import { Query } from '@varo/graphql-schema-types';
import { DocumentNode } from 'graphql';
import { useEffect, useRef, useState } from 'react';
import { CombinedError, useQuery } from 'urql';

interface PaginationBaseProps<T> {
  query: DocumentNode;
  extractDataCallback: (data: Query) => {
    edges: { node: T }[];
    pageInfo: { hasNextPage: boolean; endCursor: string | null };
  };
}

interface PageData<T> {
  loading: boolean;
  error?: CombinedError;
  data?: T[];
  loadMore: () => void;
}

const usePaginatedBaseQuery = <T>({
  query,
  extractDataCallback: extractData,
}: PaginationBaseProps<T>): PageData<T> => {
  const [after, setAfter] = useState<string | null>();
  const [transfers, setTransfers] = useState<T[]>([]);
  const fetchingMoreRef = useRef(false);

  const [{ data, fetching: loading, error }] = useQuery<Query>({
    query,
    requestPolicy: 'network-only',
    variables: {
      after: after,
      first: 20,
    },
  });

  const loadMore = () => {
    const { pageInfo } = extractData(data ?? ({} as Query));
    if (fetchingMoreRef.current || !pageInfo?.hasNextPage) {
      return;
    }
    fetchingMoreRef.current = true;
    setAfter(pageInfo?.endCursor ?? null);
  };

  useEffect(() => {
    if (data) {
      const { edges } = extractData(data);
      setTransfers((prevItems) => [
        ...prevItems,
        ...edges.map((edge) => edge.node),
      ]);
      fetchingMoreRef.current = false;
    }
  }, [data, extractData]);

  return {
    loading,
    error,
    data: transfers,
    loadMore,
  };
};

export default usePaginatedBaseQuery;
